<template>
  <div id="app">
    <header class="topbar">
      <button class="brand" type="button" @click="go('home')">
        <span class="brand-mark"><i></i><i></i></span>
        <span>calm<span>decide</span></span>
      </button>

      <nav class="desktop-nav" aria-label="Main navigation">
        <button :class="{ active: view === 'quick' }" @click="go('quick')">Quick choice</button>
        <button :class="{ active: view === 'deep' }" @click="go('deep')">Think it through</button>
        <button :class="{ active: view === 'guides' || view === 'guide' }" @click="go('guides')">Guides</button>
        <button :class="{ active: view === 'community' }" @click="go('community')">The Nudge</button>
        <button :class="{ active: view === 'journal' }" @click="go('journal')">Journal</button>
      </nav>

      <button v-if="!user" class="text-button" @click="authOpen = true">Sign in</button>
      <button v-else class="avatar" :title="user.name" @click="go('journal')">{{ userInitial }}</button>
    </header>

    <main>
      <transition name="page" mode="out-in">
        <home-view v-if="view === 'home'" key="home" @navigate="go" />
        <quick-view v-else-if="view === 'quick'" key="quick" @saved="handleSaved" />
        <deep-view v-else-if="view === 'deep'" key="deep" :user="user" @saved="handleSaved" @require-auth="authOpen = true" />
        <guides-view v-else-if="view === 'guides'" key="guides" />
        <guide-article-view v-else-if="view === 'guide'" :key="slug" :slug="slug" />
        <community-view v-else-if="view === 'community'" key="community" />
        <journal-view v-else-if="view === 'journal'" key="journal" :user="user" @require-auth="authOpen = true" @logout="logout" />
        <about-view v-else-if="view === 'about'" key="about" />
        <legal-view v-else :key="view" :page="view" />
      </transition>
    </main>

    <footer class="site-footer">
      <button class="brand footer-brand" type="button" @click="go('home')">
        <span class="brand-mark"><i></i><i></i></span>
        <span>calm<span>decide</span></span>
      </button>
      <p>Less overthinking. More living.</p>
      <nav aria-label="Legal navigation">
        <button @click="go('about')">About</button>
        <button @click="go('guides')">Guides</button>
        <button @click="go('privacy')">Privacy Policy</button>
        <button @click="go('terms')">Terms</button>
        <button @click="go('contact')">Contact Us</button>
      </nav>
      <small>© {{ currentYear }} CalmDecide</small>
    </footer>

    <nav class="mobile-nav" aria-label="Mobile navigation">
      <button :class="{ active: view === 'home' }" @click="go('home')"><span>⌂</span>Home</button>
      <button :class="{ active: view === 'quick' }" @click="go('quick')"><span>✦</span>Quick</button>
      <button :class="{ active: view === 'deep' }" @click="go('deep')"><span>◇</span>Deep</button>
      <button :class="{ active: view === 'community' }" @click="go('community')"><span>◎</span>Nudge</button>
      <button :class="{ active: view === 'journal' }" @click="go('journal')"><span>▤</span>Journal</button>
    </nav>

    <auth-modal v-if="authOpen" @close="authOpen = false" @authenticated="setUser" />
    <transition name="toast"><div v-if="toast" class="toast">{{ toast }}</div></transition>
  </div>
</template>

<script>
import HomeView from './components/HomeView.vue'
import QuickView from './components/QuickView.vue'
import DeepView from './components/DeepView.vue'
import CommunityView from './components/CommunityView.vue'
import JournalView from './components/JournalView.vue'
import AuthModal from './components/AuthModal.vue'
import LegalView from './components/LegalView.vue'
import GuidesView from './components/GuidesView.vue'
import GuideArticleView from './components/GuideArticleView.vue'
import AboutView from './components/AboutView.vue'
import guides from './content/guides'
import { clearSession, getStoredUser } from './services/api'

export default {
  name: 'App',
  components: { HomeView, QuickView, DeepView, CommunityView, JournalView, AuthModal, LegalView, GuidesView, GuideArticleView, AboutView },
  provide () {
    return {
      navigate: this.go,
      notify: this.notify
    }
  },
  data: () => ({
    view: 'home',
    slug: '',
    authOpen: false,
    user: getStoredUser(),
    toast: '',
    currentYear: new Date().getFullYear()
  }),
  computed: {
    userInitial () { return (this.user?.name || 'C').slice(0, 1).toUpperCase() }
  },
  mounted () {
    this.syncRoute()
    window.addEventListener('popstate', this.syncRoute)
    window.addEventListener('hashchange', this.syncRoute)
  },
  beforeDestroy () {
    window.removeEventListener('popstate', this.syncRoute)
    window.removeEventListener('hashchange', this.syncRoute)
  },
  methods: {
    syncRoute () {
      const next = this.routeFromLocation()
      if (this.view !== next.view) this.view = next.view
      if (this.slug !== next.slug) this.slug = next.slug
      this.updateDocumentMeta()
    },
    routeFromLocation () {
      const legacyHash = window.location.hash.replace(/^#\/?/, '')
      if (legacyHash) {
        const legacyRoute = this.routeFromParts(legacyHash.split('/').filter(Boolean))
        window.history.replaceState(legacyRoute, '', this.pathForRoute(legacyRoute.view, legacyRoute.slug))
        return legacyRoute
      }

      return this.routeFromParts(window.location.pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean))
    },
    routeFromParts (parts) {
      const segment = parts[0] || 'home'

      if (segment === 'guides' && parts[1]) {
        const guide = guides.find(guide => guide.slug === parts[1])
        return guide ? { view: 'guide', slug: guide.slug } : { view: 'guides', slug: '' }
      }

      if (this.validViews().includes(segment)) return { view: segment, slug: '' }

      window.history.replaceState({ view: 'home', slug: '' }, '', '/')
      return { view: 'home', slug: '' }
    },
    validViews () { return ['home', 'quick', 'deep', 'guides', 'community', 'journal', 'about', 'privacy', 'terms', 'contact'] },
    pathForRoute (view, slug = '') {
      if (view === 'guide') return `/guides/${slug}`
      return view === 'home' ? '/' : `/${view}`
    },
    go (view, slug = '') {
      const guide = view === 'guide' ? guides.find(guide => guide.slug === slug) : null
      const next = view === 'guide' && guide ? 'guide' : this.validViews().includes(view) ? view : 'home'
      const nextSlug = next === 'guide' ? guide.slug : ''
      const nextPath = this.pathForRoute(next, nextSlug)

      this.view = next
      this.slug = nextSlug
      if (window.location.pathname !== nextPath || window.location.hash) {
        window.history.pushState({ view: next, slug: nextSlug }, '', nextPath)
      }
      this.updateDocumentMeta()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    updateDocumentMeta () {
      const guide = this.view === 'guide' ? guides.find(guide => guide.slug === this.slug) : null
      const descriptions = {
        home: 'CalmDecide is your decision companion for quick choices, thoughtful decisions, and less overthinking.',
        quick: 'Use CalmDecide quick tools to make small low-stakes decisions faster without wasting attention.',
        deep: 'Compare important options with a weighted decision matrix and reflection prompts.',
        guides: 'Original CalmDecide guides about decision fatigue, decision matrices, reflection, and better choices.',
        community: 'Ask for a gentle nudge from the CalmDecide community through polls and short notes.',
        journal: 'Save and review past decisions in your private CalmDecide journal.',
        about: 'Learn what CalmDecide is, how it helps people make decisions, and what its limits are.',
        privacy: 'Read the CalmDecide privacy policy and data-use disclosures.',
        terms: 'Read the CalmDecide terms of use.',
        contact: 'Contact CalmDecide for support, privacy requests, reports, and feedback.'
      }
      document.title = guide ? `${guide.title} — CalmDecide` : `CalmDecide — ${this.view === 'home' ? 'Less overthinking. More living.' : this.view.charAt(0).toUpperCase() + this.view.slice(1)}`
      const description = document.querySelector('meta[name="description"]')
      if (description) description.setAttribute('content', guide ? guide.summary : descriptions[this.view])
    },
    setUser (user) {
      this.user = user
      this.authOpen = false
      this.notify(`Welcome, ${user.name}.`)
    },
    logout () {
      clearSession()
      this.user = null
      this.go('home')
    },
    handleSaved () { this.notify('Saved to your decision journal.') },
    notify (message) {
      this.toast = message
      window.clearTimeout(this.toastTimer)
      this.toastTimer = window.setTimeout(() => { this.toast = '' }, 2800)
    }
  }
}
</script>

<style>
@import './assets/styles.css';
</style>
